import type { EpochSeconds } from "./time";
import type { WorkBlockKind, WorkDay } from "./workday";

/** An attribution as the resolution needs it (spec 6.5.3). */
export interface AttributionInput {
  readonly id: string;
  readonly kind: "Switch" | "Range";
  /** The project, or `null` to remove the attribution. */
  readonly projectId: string | null;
  readonly occurredAt: EpochSeconds | null;
  readonly rangeStart: EpochSeconds | null;
  readonly rangeEnd: EpochSeconds | null;
  readonly supersedesAttributionId: string | null;
  /** When it was recorded (later ranges win), in epoch milliseconds. */
  readonly recordedAtMs: number;
  /** The activity of the project time (T-184); it never changes the working time. */
  readonly activityId?: string | null | undefined;
}

/** A block of a working day with its resolved project. */
export interface AttributedBlock {
  readonly kind: WorkBlockKind;
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
  readonly projectId: string | null;
  readonly isOpen: boolean;
  /** The activity of the deciding attribution (T-184). */
  readonly activityId?: string | null | undefined;
}

/** The project view of a working day. */
export interface ResolvedProjectDay {
  readonly day: WorkDay;
  readonly blocks: readonly AttributedBlock[];
  /** Working seconds per project. */
  readonly secondsByProject: ReadonlyMap<string, number>;
  readonly unattributedSeconds: number;
}

/**
 * Orders attributions by recording (time, then identifier).
 *
 * @param left - First attribution.
 * @param right - Second attribution.
 * @returns Negative, zero or positive.
 */
function byRecording(left: AttributionInput, right: AttributionInput): number {
  const byTime = left.recordedAtMs - right.recordedAtMs;
  const order = byTime !== 0 ? byTime : left.id < right.id ? -1 : left.id > right.id ? 1 : 0;

  return order;
}

/**
 * Resolves the project and activity of a part of a work block that starts at `at` (spec 6.5.3);
 * the activity comes with the deciding attribution, the live project has none (T-184).
 *
 * @param at - Start of the part.
 * @param shiftStart - Start of the shift of the block.
 * @param liveProjectId - Project of the last clock-in or break end.
 * @param ranges - Effective ranges.
 * @param switches - Effective switches ordered by instant and recording.
 * @returns The project (or `null`) and its activity.
 */
function projectAt(
  at: EpochSeconds,
  shiftStart: EpochSeconds,
  liveProjectId: string | null,
  ranges: readonly AttributionInput[],
  switches: readonly AttributionInput[],
): { readonly projectId: string | null; readonly activityId: string | null } {
  const range = ranges
    .filter((item) => (item.rangeStart ?? 0) <= at && (item.rangeEnd ?? 0) > at)
    .sort(byRecording)
    .at(-1);
  const lastSwitch = switches
    .filter((item) => (item.occurredAt ?? 0) <= at && (item.occurredAt ?? 0) >= shiftStart)
    .at(-1);
  const deciding = range ?? lastSwitch;
  const project =
    deciding === undefined
      ? { projectId: liveProjectId, activityId: null }
      : { projectId: deciding.projectId, activityId: deciding.activityId ?? null };

  return project;
}

/**
 * Resolves which project applies to each part of the working time (spec 6.5.3), like the backend
 * `ProjectAttributionResolver` (shared cases in `shared-test-cases/project-attribution.json`):
 * effective range (last recorded wins), then the last switch within the shift, then the live
 * project, otherwise unattributed. Breaks never carry a project (PINV-P02); the working time never
 * changes (PINV-P01).
 *
 * @param days - Working days in date order.
 * @param attributions - Attributions in any order.
 * @returns The project view of each day.
 */
export function resolveProjectDays(
  days: readonly WorkDay[],
  attributions: readonly AttributionInput[],
): ResolvedProjectDay[] {
  const superseded = new Set(
    attributions
      .map((item) => item.supersedesAttributionId)
      .filter((id): id is string => id !== null),
  );
  const effective = attributions.filter((item) => !superseded.has(item.id));
  const ranges = effective.filter((item) => item.kind === "Range");
  const switches = effective
    .filter((item) => item.kind === "Switch")
    .sort(
      (left, right) => (left.occurredAt ?? 0) - (right.occurredAt ?? 0) || byRecording(left, right),
    );
  const cuts = [
    ...switches.map((item) => item.occurredAt ?? 0),
    ...ranges.flatMap((item) => [item.rangeStart ?? 0, item.rangeEnd ?? 0]),
  ].sort((left, right) => left - right);

  const resolved = days.map((day) => {
    const blocks: AttributedBlock[] = [];
    const secondsByProject = new Map<string, number>();
    let unattributedSeconds = 0;
    const append = (block: AttributedBlock): void => {
      const previous = blocks.at(-1);

      if (block.end <= block.start) {
        return;
      }

      const continues =
        previous?.kind === block.kind &&
        previous.end === block.start &&
        previous.projectId === block.projectId &&
        (previous.activityId ?? null) === (block.activityId ?? null) &&
        !previous.isOpen;

      if (continues) {
        blocks[blocks.length - 1] = { ...previous, end: block.end, isOpen: block.isOpen };
        return;
      }

      blocks.push(block);
    };

    for (const block of day.blocks) {
      if (block.kind === "Break") {
        append({
          kind: "Break",
          start: block.start,
          end: block.end,
          projectId: null,
          isOpen: block.isOpen,
        });
        continue;
      }

      const shiftStart =
        day.shifts.filter((shift) => shift.start <= block.start).at(-1)?.start ?? block.start;
      const inside = cuts.filter((cut) => cut > block.start && cut < block.end);
      const bounds = [block.start, ...new Set(inside), block.end];

      for (let index = 0; index < bounds.length - 1; index += 1) {
        const start = bounds[index] ?? block.start;
        const end = bounds[index + 1] ?? block.end;
        const { projectId, activityId } = projectAt(
          start,
          shiftStart,
          block.projectId,
          ranges,
          switches,
        );
        const seconds = end - start;
        // Blocks without an activity keep the shape they had before T-184.
        append({
          kind: "Work",
          start,
          end,
          projectId,
          isOpen: block.isOpen && end === block.end,
          ...(activityId === null ? {} : { activityId }),
        });

        if (projectId === null) {
          unattributedSeconds += seconds;
        } else {
          secondsByProject.set(projectId, (secondsByProject.get(projectId) ?? 0) + seconds);
        }
      }
    }

    const projectDay: ResolvedProjectDay = { day, blocks, secondsByProject, unattributedSeconds };
    return projectDay;
  });

  return resolved;
}
